import { Link } from 'react-router-dom';
import { 
  Shield, 
  Award, 
  Target, 
  Heart,
  CheckCircle2,
  ArrowRight,
  Phone
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';

const TEAM_IMAGE = "https://images.unsplash.com/photo-1589723710821-989cddda124d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwzfHxwcmVzc3VyZSUyMHdhc2hpbmclMjBjb25jcmV0ZXxlbnwwfHx8fDE3NzAxNzA0OTR8MA&ixlib=rb-4.1.0&q=85";

export default function About() {
  const { t, language } = useLanguage();

  const values = [
    {
      icon: Shield,
      title: t('about.integrity'),
      description: t('about.integrityDesc')
    },
    {
      icon: Award,
      title: t('about.excellence'),
      description: t('about.excellenceDesc')
    },
    {
      icon: Target,
      title: t('about.reliability'),
      description: t('about.reliabilityDesc')
    },
    {
      icon: Heart,
      title: t('about.customerFocus'),
      description: t('about.customerFocusDesc')
    }
  ];

  const commitments = language === 'es' 
    ? [
        'Profesionales de limpieza capacitados y experimentados',
        'Productos de limpieza ecológicos disponibles',
        'Planes de limpieza personalizados para sus necesidades',
        'Personal con verificación de antecedentes y asegurado',
        'Garantía de satisfacción en todos los servicios',
        'Horarios flexibles incluyendo fines de semana'
      ]
    : [
        'Trained and experienced cleaning professionals',
        'Eco-friendly cleaning products available',
        'Customized cleaning plans for your needs',
        'Background-checked and insured staff',
        'Satisfaction guarantee on all services',
        'Flexible scheduling including weekends'
      ];

  return (
    <div data-testid="about-page">
      {/* Hero Section */}
      <section className="bg-white py-12 md:py-20 watermark-section">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto text-center animate-fadeInUp content-centered">
            <h1 className="text-4xl sm:text-5xl font-bold mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {t('about.pageTitle')} <span className="text-[#1E88E5]">{t('about.titleHighlight')}</span>
            </h1>
            <p className="text-lg text-[#334155] mb-6 leading-relaxed">
              {t('about.pageDescription1')}
            </p>
            <p className="text-lg text-[#334155] leading-relaxed">
              {t('about.pageDescription2')}
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="bg-surface py-16 md:py-24">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-4xl mx-auto">
            <div className="text-center animate-fadeInUp content-centered">
              <div className="icon-container icon-container-blue w-16 h-16 mx-auto mb-6">
                <Target size={32} />
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {t('about.missionTitle')} <span className="text-[#4CAF50]">{t('about.missionHighlight')}</span>
              </h2>
              <p className="text-lg text-[#334155] leading-relaxed max-w-3xl mx-auto">
                {t('about.missionText')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="bg-white py-16 md:py-24">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-12 md:mb-16 animate-fadeInUp content-centered">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {t('about.valuesTitle')} <span className="text-[#1E88E5]">{t('about.valuesHighlight')}</span>
            </h2>
            <p className="text-lg text-[#334155] max-w-2xl mx-auto">
              {t('about.valuesSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div 
                key={value.title}
                className="bg-surface rounded-xl p-6 text-center animate-fadeInUp"
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`value-${index}`}
              >
                <div className={`icon-container ${index % 2 === 0 ? 'icon-container-blue' : 'icon-container-green'} mx-auto mb-4`}>
                  <value.icon size={28} />
                </div>
                <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {value.title}
                </h3>
                <p className="text-[#334155] text-sm">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="bg-surface py-16 md:py-24">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="animate-fadeInUp">
              <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {t('about.commitmentTitle')} <span className="text-[#4CAF50]">{t('about.commitmentHighlight')}</span>
              </h2>
              <p className="text-lg text-[#334155] mb-8">
                {t('about.commitmentText')}
              </p>
              <ul className="space-y-4">
                {commitments.map((commitment, index) => (
                  <li 
                    key={index}
                    className="flex items-start gap-3 animate-fadeInUp"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <CheckCircle2 size={24} className="text-[#4CAF50] flex-shrink-0 mt-0.5" />
                    <span className="text-[#334155]">{commitment}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="animate-fadeInUp animation-delay-200">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-[#E2E8F0]">
                <div className="badge badge-green mb-4">{t('hero.licensed')}</div>
                <h3 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {t('about.serviceAreas')}
                </h3>
                <p className="text-[#334155] mb-4">
                  {t('about.serviceAreasText')}
                </p>
                <ul className="space-y-2 text-[#334155]">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 size={16} className="text-[#1E88E5]" />
                    {t('about.losAngeles')}
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 size={16} className="text-[#1E88E5]" />
                    {t('about.sanFernando')}
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 size={16} className="text-[#1E88E5]" />
                    {t('about.sylmar')}
                  </li>
                </ul>
                <div className="mt-6 pt-6 border-t border-[#E2E8F0]">
                  <p className="text-sm text-[#334155] mb-2">{t('about.availableMonSat')}</p>
                  <p className="font-semibold text-[#0F172A]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    7:00 AM - 10:00 PM
                  </p>
                  <p className="text-sm text-[#4CAF50] font-medium mt-1">
                    Emergency cleaning available during business hours. We may also assist outside scheduled hours for urgent needs.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[#E8F5E9]">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 text-center animate-fadeInUp">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
            {t('about.ctaTitle')} <span className="text-[#4CAF50]">{t('about.ctaHighlight')}</span>
          </h2>
          <p className="text-lg text-[#334155] max-w-2xl mx-auto mb-8">
            {t('about.ctaText')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="btn-secondary text-base" data-testid="about-cta-quote">
                {t('about.getFreeQuote')}
                <ArrowRight size={18} />
              </Button>
            </Link>
            <a href="tel:+13233780061">
              <Button className="btn-outline text-base" data-testid="about-cta-call">
                <Phone size={18} />
                (323) 378-0061
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
